import { useEffect, useState, Fragment } from 'react';
import { API, formatCurrency, formatDateBR } from './constants';
import { Archive, Info, CaretDown, CaretRight, FileText, WarningCircle } from '@phosphor-icons/react';
import { useNarrow, MobileRow, KV } from '@/components/historico/MobileRows';

/**
 * Painel somente leitura do historico importado para um dia.
 * Usado dentro do CaixaDia quando ha dados em /api/historico/dia/{data}.
 */
export const HistoricoDiaPanel = ({ data, selectedDate, variant = 'full' }) => {
  // variant: 'full' = ocupa o lugar do caixa (dia sem caixa nativo)
  //          'secao' = secao complementar abaixo do caixa nativo
  const [expand, setExpand] = useState({});
  const narrow = useNarrow();
  if (!data || !data.tem_historico) return null;

  const t = data.totais || {};
  const post = data.pagamentos_pagos_em_data_posterior || { qtd: 0, valor_centavos: 0 };

  const toggle = (c) => setExpand((p) => ({ ...p, [c]: !p[c] }));

  const header = (
    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Archive size={18} weight="regular" className="text-[#6B21A8]" />
        <h3 className="text-base font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
          {variant === 'full' ? 'Caixa (historico importado)' : 'Historico importado neste dia'}
        </h3>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-lg uppercase tracking-wide"
          style={{ background: '#F3E8FF', color: '#6B21A8' }}>
          Historico
        </span>
      </div>
      <div className="text-[11px] text-[#71717A] flex items-center gap-2 flex-wrap">
        <FileText size={12} /> Lote: <span className="font-medium text-[#18181B]">{data.lote || (data.lotes||[]).join(', ')}</span>
        <span className="mx-1">·</span>
        {formatDateBR(selectedDate)}
      </div>
    </div>
  );

  const kpis = (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="historico-kpis">
      <KPI label="Total pago" value={formatCurrency((t.total_pago_centavos || 0) / 100)} />
      <KPI label="Servicos" value={formatCurrency((t.total_servicos_centavos || 0) / 100)} />
      <KPI label="Produtos" value={formatCurrency((t.total_produtos_centavos || 0) / 100)} />
      <KPI label="Comandas" value={t.comandas_count || 0} testId="hist-kpi-comandas"
        sub={`${t.pagamentos_count || 0} pagamentos${t.comandas_sem_pagamento_count ? ` · ${t.comandas_sem_pagamento_count} sem pagamento (R$ 0,00)` : ''}`} />
    </div>
  );

  const porFp = (
    <div className="bg-white rounded-xl border border-[#E4E4E7] overflow-hidden mb-4">
      <div className="px-4 py-2 border-b border-[#E4E4E7] text-xs font-semibold text-[#71717A] uppercase tracking-wide">
        Por forma de pagamento
      </div>
      <table className="w-full text-sm">
        <tbody>
          {(data.por_forma_pagamento || []).map((f) => (
            <tr key={f.forma_pagamento} className="border-t border-[#F4F4F5] first:border-t-0" data-testid={`hist-fp-${f.forma_pagamento}`}>
              <td className="px-3 sm:px-4 py-2 text-[#18181B] break-words">{f.forma_pagamento}</td>
              <td className="px-2 sm:px-4 py-2 text-right text-[#71717A] w-16 whitespace-nowrap">{f.qtd}×</td>
              <td className="px-3 sm:px-4 py-2 text-right font-medium whitespace-nowrap">{formatCurrency((f.valor_centavos || 0) / 100)}</td>
            </tr>
          ))}
          {(!data.por_forma_pagamento || data.por_forma_pagamento.length === 0) && (
            <tr><td className="px-4 py-3 text-xs text-[#A1A1AA]">Nenhum pagamento.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const aviso = post.qtd > 0 ? (
    <div className="mb-4 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="hist-aviso-pag-posterior">
      <Info size={14} className="mt-0.5" />
      <span>
        {post.qtd} pagamento(s) deste dia foram pagos em data posterior ·
        total {formatCurrency((post.valor_centavos || 0) / 100)}
      </span>
    </div>
  ) : null;

  const comandas = (
    <div className="bg-white rounded-xl border border-[#E4E4E7] overflow-hidden">
      <div className="px-4 py-2 border-b border-[#E4E4E7] text-xs font-semibold text-[#71717A] uppercase tracking-wide">
        Comandas do dia ({t.comandas_count || 0}{t.comandas_sem_pagamento_count ? ` + ${t.comandas_sem_pagamento_count} sem pagamento` : ''})
      </div>
      {narrow ? (
        <div className="p-2 space-y-2" data-testid="hist-comandas-mobile">
          {data.comandas.map((c) => {
            const open = !!expand[c.comanda];
            return (
              <MobileRow key={c.comanda} testId={`hist-comanda-${c.comanda}`}
                title={<>#{c.comanda} {c.cliente_origem || <span className="text-[#A1A1AA]">—</span>}
                  {c.sem_pagamento && <span className="ml-1 whitespace-nowrap text-[9px] px-1 py-0.5 rounded uppercase tracking-wide bg-[#F4F4F5] text-[#71717A]" data-testid={`hist-sem-pag-${c.comanda}`}>sem pagamento</span>}</>}
                value={formatCurrency((c.total_pagamentos_centavos || 0) / 100)}
                meta={[['Itens', formatCurrency((c.total_itens_centavos || 0) / 100)]]}>
                <button type="button" onClick={() => toggle(c.comanda)} data-testid={`hist-toggle-${c.comanda}`}
                  className="mt-1 min-h-[36px] text-xs text-[#1B4332] inline-flex items-center gap-1">
                  {open ? <CaretDown size={10} /> : <CaretRight size={10} />}{open ? 'Fechar' : 'Ver itens e pagamentos'}
                </button>
                {open && <ComandaDetalheMobile c={c} />}
              </MobileRow>
            );
          })}
        </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-[#71717A] text-xs">
            <tr>
              <th className="text-left px-4 py-2 w-16">#</th>
              <th className="text-left px-4 py-2">Cliente (origem)</th>
              <th className="text-right px-4 py-2">Itens</th>
              <th className="text-right px-4 py-2">Pago</th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody>
            {data.comandas.map((c) => {
              const open = !!expand[c.comanda];
              return (
                <Fragment key={c.comanda}>
                  <tr className="border-t border-[#F4F4F5]" data-testid={`hist-comanda-${c.comanda}`}>
                    <td className="px-4 py-2 font-medium">
                      {c.comanda}
                      {c.sem_pagamento && (
                        <span className="ml-1 whitespace-nowrap text-[9px] px-1 py-0.5 rounded uppercase tracking-wide bg-[#F4F4F5] text-[#71717A]" data-testid={`hist-sem-pag-${c.comanda}`}>
                          sem pagamento
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">{c.cliente_origem || <span className="text-[#A1A1AA]">—</span>}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency((c.total_itens_centavos || 0) / 100)}</td>
                    <td className="px-4 py-2 text-right font-medium">{formatCurrency((c.total_pagamentos_centavos || 0) / 100)}</td>
                    <td className="px-2 py-2 text-right">
                      <button type="button" onClick={() => toggle(c.comanda)} data-testid={`hist-toggle-${c.comanda}`}
                        className="text-xs text-[#1B4332] hover:underline inline-flex items-center gap-1">
                        {open ? <CaretDown size={10} /> : <CaretRight size={10} />}
                        {open ? 'Fechar' : 'Ver'}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-[#FBFAF8]">
                      <td colSpan={5} className="px-4 py-3">                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-[10px] font-semibold text-[#71717A] uppercase mb-1">Itens</div>
                            <table className="w-full text-xs">
                              <tbody>
                                {c.itens.map((it, idx) => (
                                  <tr key={idx} className="border-b border-[#F4F4F5]">
                                    <td className="py-1 pr-2 text-[#71717A] w-16">{it.tipo}</td>
                                    <td className="py-1">
                                      <div className="text-[#18181B]">{it.descricao_origem}</div>
                                      <div className="text-[10px] text-[#A1A1AA]">
                                        {it.profissional_origem || '(sem profissional no Avec)'}{it.categoria_origem ? ` · ${it.categoria_origem}` : ''}
                                      </div>
                                    </td>
                                    <td className="py-1 text-right text-[#71717A] w-10">{it.qtd}×</td>
                                    <td className="py-1 text-right font-medium w-24">{formatCurrency((it.total_centavos || 0) / 100)}</td>
                                  </tr>
                                ))}
                                {c.itens.length === 0 && (
                                  <tr><td colSpan={4} className="py-1 text-[10px] text-[#A1A1AA]">Sem itens.</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div>
                            <div className="text-[10px] font-semibold text-[#71717A] uppercase mb-1">Pagamentos</div>
                            <table className="w-full text-xs">
                              <tbody>
                                {c.pagamentos.map((p, idx) => {
                                  const posterior = p.pago_em_data_posterior;
                                  return (
                                    <tr key={idx} className="border-b border-[#F4F4F5]">
                                      <td className="py-1 w-8 text-[#71717A]">#{p.seq_pagamento}</td>
                                      <td className="py-1">
                                        {p.forma_pagamento}{p.bandeira ? ` (${p.bandeira})` : ''}
                                        {posterior && (
                                          <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded uppercase tracking-wide"
                                            style={{ background: '#FEF3C7', color: '#92400E' }}>
                                            data posterior
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-1 text-right text-[10px] text-[#71717A] w-20">{p.data_pagamento}</td>
                                      <td className="py-1 text-right font-medium w-24">{formatCurrency((p.valor_centavos || 0) / 100)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );

  if (variant === 'secao') {
    return (
      <section className="mb-6 animate-fade-in-up" data-testid="historico-dia-secao">
        <div className="salonup-card p-4 sm:p-5">
          {header}
          <p className="text-xs text-[#71717A] mb-3 flex items-start gap-1">
            <WarningCircle size={12} className="mt-0.5" />
            Esta secao mostra o historico importado deste dia, separada do caixa nativo. Totais nao se somam entre as duas partes.
          </p>
          {kpis}
          {aviso}
          <div className="grid md:grid-cols-5 gap-4">
            <div className="md:col-span-2">{porFp}</div>
            <div className="md:col-span-3">{comandas}</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-6 animate-fade-in-up" data-testid="historico-dia-full">
      <div className="salonup-card p-4 sm:p-6">
        {header}
        {kpis}
        {aviso}
        <div className="grid md:grid-cols-5 gap-4">
          <div className="md:col-span-2">{porFp}</div>
          <div className="md:col-span-3">{comandas}</div>
        </div>
      </div>
    </section>
  );
};

const ComandaDetalheMobile = ({ c }) => (
  <div className="mt-1 border-t border-[#F4F4F5] pt-2 space-y-2">
    <div>
      <div className="text-[10px] font-semibold text-[#71717A] uppercase">Itens</div>
      {c.itens.map((it, idx) => (
        <div key={idx} className="py-1 border-b border-[#F4F4F5] last:border-b-0">
          <KV label={<span className="text-[#18181B]">{it.descricao_origem}</span>} value={formatCurrency((it.total_centavos || 0) / 100)} />
          <div className="text-[10px] text-[#A1A1AA] break-words">
            {it.tipo} · {it.qtd}× · {it.profissional_origem || '(sem profissional no Avec)'}{it.categoria_origem ? ` · ${it.categoria_origem}` : ''}
          </div>
        </div>
      ))}
      {c.itens.length === 0 && <div className="text-[10px] text-[#A1A1AA]">Sem itens.</div>}
    </div>
    <div>
      <div className="text-[10px] font-semibold text-[#71717A] uppercase">Pagamentos</div>
      {c.pagamentos.map((p, idx) => (
        <div key={idx} className="py-1 border-b border-[#F4F4F5] last:border-b-0">
          <KV label={`#${p.seq_pagamento} ${p.forma_pagamento}${p.bandeira ? ` (${p.bandeira})` : ''}`} value={formatCurrency((p.valor_centavos || 0) / 100)} />
          <div className="text-[10px] text-[#A1A1AA]">{p.data_pagamento}
            {p.pago_em_data_posterior && <span className="ml-1 text-[9px] px-1 py-0.5 rounded uppercase tracking-wide" style={{ background: '#FEF3C7', color: '#92400E' }}>data posterior</span>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const KPI = ({ label, value, sub, testId }) => (
  <div className="rounded-xl border border-[#E4E4E7] p-3 bg-white" data-testid={testId}>
    <div className="text-[10px] uppercase text-[#71717A] tracking-wide">{label}</div>
    <div className="text-base font-semibold text-[#18181B] mt-0.5" style={{ fontFamily: 'Outfit' }}>{value}</div>
    {sub && <div className="text-[10px] text-[#71717A] mt-0.5">{sub}</div>}
  </div>
);

/**
 * Hook para carregar o historico de um dia.
 * Retorna {historico, loading}. Null quando nao ha historico.
 */
export const useHistoricoDoDia = (date) => {
  const [historico, setHistorico] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/historico/dia/${date}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setHistorico(d && d.tem_historico ? d : null); })
      .catch(() => { if (!cancelled) setHistorico(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);
  return { historico, loading };
};
